import { create } from 'zustand';
import type { Attachment } from 'features/chat/types/chat';

interface AssistantFileViewerState {
  file: Attachment | null;
  /** HTML opened in the dashboard panel. Separate from the file preview. */
  dashboardHtmlFile: Attachment | null;
  isExpanded: boolean;
  /** The open conversation is a dashboard: HTML output opens in the dashboard panel. */
  isDashboardConversation: boolean;
  /** The user closed the canvas. A later HTML file opens it again. */
  isDashboardCanvasDismissed: boolean;
  /** A conversation whose live turn delivered a dashboard; it shows as a dashboard chat at once. */
  liveDashboardConversationId: string | null;
  /** The open thread holds a `.dashboard` file: HTML pages then open in the file preview. */
  hasDashboardFileInThread: boolean;
  openFile: (file: Attachment) => void;
  openDashboardHtml: (file: Attachment) => void;
  closeFile: () => void;
  closeDashboardHtml: () => void;
  setDashboardCanvasDismissed: (isDashboardCanvasDismissed: boolean) => void;
  setExpanded: (isExpanded: boolean) => void;
  toggleExpanded: () => void;
  setDashboardConversation: (isDashboardConversation: boolean) => void;
  markLiveDashboard: (conversationId: string) => void;
  setDashboardFileInThread: (hasDashboardFileInThread: boolean) => void;
}

export const useAssistantFileViewerStore = create<AssistantFileViewerState>((set) => ({
  file: null,
  dashboardHtmlFile: null,
  isExpanded: false,
  isDashboardConversation: false,
  isDashboardCanvasDismissed: false,
  liveDashboardConversationId: null,
  hasDashboardFileInThread: false,
  openFile: (file) => set({ file, isExpanded: false, dashboardHtmlFile: null }),
  openDashboardHtml: (file) =>
    set({
      dashboardHtmlFile: file,
      file: null,
      isExpanded: false,
      isDashboardCanvasDismissed: false,
    }),
  closeFile: () => set({ file: null, isExpanded: false }),
  closeDashboardHtml: () => set({ dashboardHtmlFile: null }),
  setDashboardCanvasDismissed: (isDashboardCanvasDismissed) =>
    set(
      isDashboardCanvasDismissed
        ? { isDashboardCanvasDismissed: true, dashboardHtmlFile: null }
        : { isDashboardCanvasDismissed: false }
    ),
  setExpanded: (isExpanded) => set({ isExpanded }),
  toggleExpanded: () => set((state) => ({ isExpanded: !state.isExpanded })),
  setDashboardConversation: (isDashboardConversation) => set({ isDashboardConversation }),
  markLiveDashboard: (liveDashboardConversationId) => set({ liveDashboardConversationId }),
  setDashboardFileInThread: (hasDashboardFileInThread) => set({ hasDashboardFileInThread }),
}));
